// Günlük alım özeti (2026-10-05) — hastanın mobilde işaretlediği "yedim / atladım"
// ve girdiği besinlerden gün gün kalori, hedefe uyum. Sunucu: diet/domain/intake.js.

import React from "react";
import { Box, LinearProgress, Skeleton, Typography, alpha } from "@mui/material";
import { COLORS, TYPO } from "../../../styles/uiConstants";

const kcal = (v) => `${Math.round(Number(v) || 0).toLocaleString("tr-TR")} kcal`;
const dayLabel = (d) =>
  new Date(`${d}T12:00:00`).toLocaleDateString("tr-TR", { weekday: "short", day: "numeric", month: "short" });

function Kpi({ label, value, sub }) {
  return (
    <Box sx={{ minWidth: 150 }}>
      <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1.35rem", color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
      {sub ? <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>{sub}</Typography> : null}
    </Box>
  );
}

export default function IntakeSummary({ data, loading, palette }) {
  const tone = palette?.primary || COLORS.slate700;
  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", gap: 3, py: 1 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" width={140} height={56} />
        ))}
      </Box>
    );
  }
  if (!data?.plan) {
    return (
      <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
        Aktif beslenme planı yok. Plan olunca hasta mobilde öğünlerini işaretler ve günlük alım burada görünür.
      </Typography>
    );
  }
  const a = data.adherence || {};
  const target = a.target;
  const days = [...(data.days || [])].reverse();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        <Kpi label="Kayıt girilen gün" value={`${a.loggedDays}/${a.days}`} sub={data.plan.mode === "free" ? "Serbest plan" : "Öğün planlı"} />
        <Kpi
          label="Hedefte gün"
          value={target ? `${a.onTargetDays}/${a.loggedDays || 0}` : "—"}
          sub={target ? `Hedef ${kcal(target)} ±%10` : "Kalori hedefi yok"}
        />
        <Kpi label="Ortalama alım" value={a.avgCalories != null ? kcal(a.avgCalories) : "—"} sub="Kayıt girilen günlerde" />
        <Kpi label="Atlanan öğün" value={a.skippedMeals} />
      </Box>
      <Box>
        {days.map((d) => {
          const pct = target ? Math.min(1.5, d.calories / target) : null;
          const off = target && d.logged ? Math.abs(d.calories - target) > target * 0.1 : false;
          const barColor = !d.logged ? COLORS.slate200 : off ? COLORS.warningDark : COLORS.successDark;
          return (
            <Box
              key={d.date}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "90px 1fr", md: "110px 1fr 200px" },
                alignItems: "center",
                gap: 2,
                py: 0.75,
                borderBottom: `1px solid ${COLORS.slate100}`,
              }}
            >
              <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate600 }}>{dayLabel(d.date)}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <Box sx={{ flex: 1, maxWidth: 360 }}>
                  {pct != null ? (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.round((pct / 1.5) * 100))}
                      sx={{ height: 6, borderRadius: 3, bgcolor: alpha(tone, 0.08), "& .MuiLinearProgress-bar": { bgcolor: barColor } }}
                    />
                  ) : null}
                </Box>
                <Typography sx={{ fontSize: TYPO.label, color: d.logged ? COLORS.slate800 : COLORS.slate400, fontVariantNumeric: "tabular-nums", minWidth: 120 }}>
                  {d.logged ? `${kcal(d.calories)}${target ? ` / ${kcal(target)}` : ""}` : "Kayıt yok"}
                </Typography>
              </Box>
              <Typography sx={{ display: { xs: "none", md: "block" }, fontSize: TYPO.caption, color: COLORS.slate500 }}>
                {d.plannedMeals ? `${d.eatenMeals}/${d.plannedMeals} öğün yendi` : ""}
                {d.skippedMeals ? `${d.plannedMeals ? " · " : ""}${d.skippedMeals} atlandı` : ""}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
